import { useEffect, useState } from 'react';
import { GLTF, GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import type { EnhancedGLTF, LoadedCharacterMesh } from '@cartwhl/3d/gltf/types';

import { createSignedUrlLoadingManager } from '../utils/signedUrlCache';
import { loadAndEnrichFBX } from './loadCharacterMesh';
import { loadAndEnrichGLTF } from './useGLTFWithCredentials';

export type CharacterMeshFormat = 'fbx' | 'glb' | 'gltf';

function inferFormat(
  path: string,
  hint?: CharacterMeshFormat,
): CharacterMeshFormat {
  if (hint) return hint;
  const ext = path.split('.').pop()?.toLowerCase();
  if (ext === 'fbx') return 'fbx';
  if (ext === 'gltf') return 'gltf';
  return 'glb';
}

export type LoadedCharacter = EnhancedGLTF | LoadedCharacterMesh;
export type TransformLoadedMesh = (
  model: LoadedCharacter,
) => void | Promise<void>;
export type LoadFBXMesh = (path: string) => Promise<LoadedCharacter>;

/**
 * Load a character mesh (GLB/GLTF or FBX) with the same credential and
 * caching behavior for both. Dispatches by path extension or optional format hint
 * (required for blob URLs which have no extension).
 */
export default function useCharacterMeshWithCredentials(
  path: string | undefined,
  setLoading?: (loading: boolean) => void,
  options?: {
    /** Required when path is a blob URL (no extension). */
    format?: CharacterMeshFormat;
    /** Optional modifiers applied only for GLTF/GLB. */
    modifiers?: Array<(model: GLTF) => void>;
    /**
     * When true, fetches the sidecar bones `.json` like `useGLTFWithCredentials`.
     * Character mesh loading defaults to false to avoid extra requests.
     */
    loadBonesMap?: boolean;
    /** Optional pre-render mutation hook for loaded FBX/GLTF scenes. */
    transformLoadedMesh?: TransformLoadedMesh;
    /** Optional FBX loader override for callers that need a specialized parser. */
    loadFBX?: LoadFBXMesh;
  },
): LoadedCharacter | null {
  const [model, setModel] = useState<LoadedCharacter | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!path) return;

    const format = inferFormat(path, options?.format);
    const transformLoadedMesh = options?.transformLoadedMesh;
    const loadFBX = options?.loadFBX ?? loadAndEnrichFBX;
    setLoading?.(true);

    let cancelled = false;

    const load = (async () => {
      const data =
        format === 'fbx'
          ? await loadFBX(path)
          : await loadAndEnrichGLTF(path, options?.modifiers, {
              loadBonesMap: options?.loadBonesMap ?? false,
            });
      if (transformLoadedMesh) {
        await transformLoadedMesh(data);
      }
      return data;
    })();

    load
      .then((data) => {
        if (cancelled) return;
        setModel(data);
        setLoading?.(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
        setLoading?.(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    path,
    options?.format,
    options?.loadBonesMap,
    options?.transformLoadedMesh,
    options?.loadFBX,
  ]);

  if (error) throw error;
  return model;
}

export { loadAndEnrichFBX };
